import React, { useState, useRef, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import SidebarItem from './SidebarItem';

interface SubItem {
  name: string;
  href: string;
  description: string;
}

interface NavigationItem {
  name: string;
  href: string;
  icon: React.ComponentType<any>;
  activeIcon?: React.ComponentType<any>;
  permission: string;
  description?: string;
  badge?: string;
  badgeColor?: string;
  priority?: boolean;
  subItems?: SubItem[];
}

interface SidebarGroupProps {
  id: string;
  name: string;
  icon: React.ComponentType<any>;
  items: NavigationItem[];
  href?: string;
  permission?: string;
  isCollapsed: boolean;
  isGroupCollapsed: boolean;
  onToggleGroup: () => void;
  hasAccess: (item: any) => boolean;
  isPopoverActive: boolean;
  onPopoverChange: (isActive: boolean) => void;
}

const SidebarGroup: React.FC<SidebarGroupProps> = ({
  id,
  name,
  icon: GroupIcon,
  items,
  href,
  permission,
  isCollapsed,
  isGroupCollapsed,
  onToggleGroup,
  hasAccess,
  isPopoverActive,
  onPopoverChange,
}) => {
  const location = useLocation();
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [hoverTimeout, setHoverTimeout] = useState<NodeJS.Timeout | null>(null);
  const groupRef = useRef<HTMLDivElement>(null);

  const isActiveRoute = (href: string) => {
    // Exact match
    if (location.pathname === href) {
      return true;
    }
    // Check if it's a child path (starts with href + '/')
    // But exclude cases where href is a parent of another menu item
    // For example, /staff should not match /staff/new
    if (location.pathname.startsWith(href + '/')) {
      // Special handling: if href is /staff, don't match /staff/new
      if (href === '/staff' && location.pathname.startsWith('/staff/new')) {
        return false;
      }
      return true;
    }
    return false;
  };

  const handleGroupClick = () => {
    if (!isCollapsed) {
      onToggleGroup();
    }
  };

  const handleGroupHover = (event: React.MouseEvent) => {
    if (isCollapsed && !hasDirectHref) {
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
        setHoverTimeout(null);
      }

      const rect = event.currentTarget.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      // Calculate estimated menu height based on number of items
      const estimatedItemHeight = 65; // Approximate height per item
      const titleHeight = 50;
      const padding = 16;
      const estimatedMenuHeight = Math.min(
        accessibleItems.length * estimatedItemHeight + titleHeight + padding,
        400 // max-h-[400px]
      );

      // Calculate vertical position - keep it closer to the sidebar
      let yPosition = rect.top + rect.height / 2;

      // Check if popover would overflow top
      if (yPosition - estimatedMenuHeight / 2 < 20) {
        yPosition = estimatedMenuHeight / 2 + 20; // Add 20px padding from top
      }

      // Check if popover would overflow bottom
      if (yPosition + estimatedMenuHeight / 2 > viewportHeight - 20) {
        yPosition = viewportHeight - estimatedMenuHeight / 2 - 20; // Add 20px padding from bottom
      }

      setTooltipPosition({
        x: rect.right,
        y: yPosition,
      });
      onPopoverChange(true);
    }
  };

  const handleGroupLeave = () => {
    // Don't close immediately - let the popover handle it
  };

  const handlePopoverEnter = () => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
    onPopoverChange(true);
  };

  const handlePopoverLeave = () => {
    // Close immediately when leaving the popover area
    onPopoverChange(false);
  };

  const handleClosePopover = () => {
    onPopoverChange(false);
  };

  const accessibleItems = items.filter((item) => hasAccess(item));

  // Check if group has direct href (no items) and user has permission
  const hasDirectHref = href && (!permission || hasAccess({ permission }));
  const hasNoItems = items.length === 0;

  // Check if any item in this group is active, or if group href is active
  const hasActiveItem = hasDirectHref 
    ? isActiveRoute(href)
    : accessibleItems.some((item) => {
        const isItemActive = isActiveRoute(item.href);
        const hasActiveSubItem = item.subItems?.some(subItem => isActiveRoute(subItem.href)) || false;
        return isItemActive || hasActiveSubItem;
      });

  // If group has no items and no direct href, or no accessible items, don't render
  if (!hasDirectHref && accessibleItems.length === 0) {
    return null;
  }

  // If group has direct href but no permission, don't render
  if (hasDirectHref && permission && !hasAccess({ permission })) {
    return null;
  }

  return (
    <div ref={groupRef} className="mb-1 sm:mb-2 relative">
      {/* Group Header */}
      {hasDirectHref ? (
        <NavLink
          to={href!}
          onMouseEnter={handleGroupHover}
          onMouseLeave={handleGroupLeave}
          className={`w-full flex items-center px-2 py-1.5 text-xs sm:text-sm font-medium rounded-lg transition-all duration-300 group border touch-manipulation ${
            isCollapsed ? 'justify-center' : 'justify-start'
          } ${
            hasActiveItem
              ? 'bg-gradient-to-r from-gray-100 to-gray-200 text-gray-800 shadow-md border-gray-300'
              : 'text-slate-600 hover:bg-gradient-to-r hover:from-gray-200 hover:to-gray-300 hover:text-gray-800 border border-slate-200/50 hover:border-gray-300/50'
          }`}
        >
          <div
            className={`flex items-center ${isCollapsed ? 'justify-center' : 'space-x-2 sm:space-x-3'} ${isCollapsed ? '' : 'flex-1 min-w-0'}`}
          >
            <div className="h-7 w-7 sm:h-8 sm:w-8 lg:h-9 lg:w-9 rounded-lg flex items-center justify-center transition-all duration-300 flex-shrink-0 bg-gradient-to-br from-slate-100 to-slate-200 group-hover:from-gray-200 group-hover:to-gray-300 shadow-sm group-hover:shadow-md">
              <GroupIcon
                className={`h-3.5 w-3.5 sm:h-4 sm:w-4 lg:h-4.5 lg:w-4.5 transition-colors duration-300 ${
                  hasActiveItem ? 'text-gray-700' : 'text-slate-500 group-hover:text-gray-700'
                }`}
              />
            </div>
            {!isCollapsed && (
              <span className="font-medium break-words text-xs sm:text-sm text-left">
                {name}
              </span>
            )}
          </div>
        </NavLink>
      ) : (
        <button
          onClick={handleGroupClick}
          onMouseEnter={handleGroupHover}
          onMouseLeave={handleGroupLeave}
          className={`w-full flex items-center px-2 py-1.5 text-xs sm:text-sm font-medium rounded-lg transition-all duration-300 group border touch-manipulation ${
            isCollapsed ? 'justify-center' : 'justify-start'
          } ${
            hasActiveItem
              ? 'bg-gradient-to-r from-gray-100 to-gray-200 text-gray-800 shadow-md border-gray-300'
              : 'text-slate-600 hover:bg-gradient-to-r hover:from-gray-200 hover:to-gray-300 hover:text-gray-800 border border-slate-200/50 hover:border-gray-300/50'
          }`}
        >
        <div
          className={`flex items-center ${isCollapsed ? 'justify-center' : 'space-x-2 sm:space-x-3'} ${isCollapsed ? '' : 'flex-1 min-w-0'}`}
        >
          <div className="h-7 w-7 sm:h-8 sm:w-8 lg:h-9 lg:w-9 rounded-lg flex items-center justify-center transition-all duration-300 flex-shrink-0 bg-gradient-to-br from-slate-100 to-slate-200 group-hover:from-gray-200 group-hover:to-gray-300 shadow-sm group-hover:shadow-md">
            <GroupIcon
              className={`h-3.5 w-3.5 sm:h-4 sm:w-4 lg:h-4.5 lg:w-4.5 transition-colors duration-300 ${
                hasActiveItem ? 'text-gray-700' : 'text-slate-500 group-hover:text-gray-700'
              }`}
            />
          </div>
          {!isCollapsed && (
            <span className="font-medium break-words text-xs sm:text-sm text-left">
              {name}
            </span>
          )}
        </div>
        {!isCollapsed && (
          <div className="ml-auto">
            {isGroupCollapsed ? (
              <ChevronRightIcon className={`h-3.5 w-3.5 sm:h-4 sm:w-4 lg:h-4.5 lg:w-4.5 transition-colors duration-300 flex-shrink-0 ${
                hasActiveItem ? 'text-gray-700' : 'text-slate-400 group-hover:text-gray-600'
              }`} />
            ) : (
              <ChevronDownIcon className={`h-3.5 w-3.5 sm:h-4 sm:w-4 lg:h-4.5 lg:w-4.5 transition-colors duration-300 flex-shrink-0 ${
                hasActiveItem ? 'text-gray-700' : 'text-slate-400 group-hover:text-gray-600'
              }`} />
            )}
          </div>
        )}
      </button>
      )}

      {/* Group Items - Expanded View */}
      {!isCollapsed && !isGroupCollapsed && (
        <div className="ml-3 sm:ml-4 lg:ml-5 mt-0.5 sm:mt-1 space-y-0.5 animate-expandDown">
          {accessibleItems.map((item) => {
            const isActive = isActiveRoute(item.href);
            return (
              <SidebarItem
                key={item.name}
                name={item.name}
                href={item.href}
                icon={item.icon}
                activeIcon={item.activeIcon}
                description={item.description}
                badge={item.badge}
                badgeColor={item.badgeColor}
                subItems={item.subItems}
                isCollapsed={false}
                isActive={isActive}
                onNavigate={handleClosePopover}
              />
            );
          })}
        </div>
      )}

      {/* Collapsed State Hover Popover */}
      {isCollapsed && isPopoverActive && !hasDirectHref && (
        <>
          {/* Invisible bridge to prevent hover gaps */}
          <div
            className="fixed bg-transparent pointer-events-auto z-[999999999]"
            style={{
              left: `${tooltipPosition.x - 30}px`,
              top: `${tooltipPosition.y - 150}px`,
              width: '30px',
              height: '300px',
            }}
            onMouseEnter={handlePopoverEnter}
            onMouseLeave={handlePopoverLeave}
          />

          <div
            className="fixed bg-gradient-to-br from-white to-slate-50 rounded-xl shadow-2xl border border-slate-200/50 min-w-72 max-w-80  overflow-y-auto p-2 animate-slideIn z-[60]"
            style={{
              left: `${tooltipPosition.x}px`,
              top: `${tooltipPosition.y}px`,
              transform: 'translateY(-50%)',
              maxHeight: 'calc(100vh - 40px)'
            }}
            onMouseEnter={handlePopoverEnter}
            onMouseLeave={handlePopoverLeave}
          >
            {/* Arrow pointer */}
            <div className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-2 w-4 h-4 bg-white border-l border-t border-slate-200/50 rotate-[-45deg]" />

            {/* Group Title */}
            <div className="px-3 py-2 mb-1">
              <h3 className="text-sm font-bold text-slate-800 text-left">{name}</h3>
            </div>

            {/* Menu Items */}
            <div className="space-y-1">
              {accessibleItems.map((item) => {
                const isActive = isActiveRoute(item.href);
                const hasActiveSubItem = item.subItems?.some(subItem => isActiveRoute(subItem.href)) || false;
                const isParentActive = isActive || hasActiveSubItem;
                const Icon = isParentActive && item.activeIcon ? item.activeIcon : item.icon;

                return (
                  <div key={item.name} className="relative">
                    <NavLink
                      to={item.href === '#' ? '#' : item.href}
                      onClick={(e) => {
                        if (item.href === '#') {
                          e.preventDefault();
                        } else {
                          // Close popover when navigating
                          handleClosePopover();
                        }
                      }}
                      className={`group relative flex items-center px-2 py-1 text-xs sm:text-sm font-medium rounded-lg transition-all duration-200 touch-manipulation ${
                        isParentActive
                          ? 'bg-gradient-to-r from-gray-100 to-gray-200 text-gray-800 shadow-md border border-gray-300'
                          : 'text-slate-600 active:bg-gradient-to-r active:from-gray-200 active:to-gray-300 active:text-gray-800 lg:hover:bg-gradient-to-r lg:hover:from-gray-200 lg:hover:to-gray-300 lg:hover:text-gray-800'
                      }`}
                    >
                      {isParentActive && (
                        <div className="absolute left-0 top-1/2 transform -translate-y-1/2 w-1 h-6 bg-gray-400 rounded-r-full" />
                      )}

                      <div className="mr-2 sm:mr-2 h-5 w-5 sm:h-6 sm:w-6 lg:h-7 lg:w-7 rounded-lg flex items-center justify-center transition-all duration-200 flex-shrink-0 bg-gradient-to-br from-slate-100 to-slate-200 active:from-gray-200 active:to-gray-300 lg:group-hover:from-gray-200 lg:group-hover:to-gray-300 shadow-sm">
                        <Icon
                          className={`h-3 w-3 sm:h-3.5 sm:w-3.5 transition-colors duration-200 ${
                            isParentActive
                              ? 'text-gray-700'
                              : 'text-slate-500 active:text-gray-700 lg:group-hover:text-gray-700'
                          }`}
                        />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-sm">{item.name}</span>
                          {item.badge && (
                            <span
                              className={`ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-semibold text-white flex-shrink-0 ${
                                item.badgeColor || 'bg-red-500'
                              }`}
                            >
                              {item.badge}
                            </span>
                          )}
                        </div>
                      </div>
                    </NavLink>

                    {/* Sub-items */}
                    {item.subItems && item.subItems.length > 0 && (
                      <div className="ml-6 mt-1 space-y-0.5">
                        {item.subItems.map((subItem) => {
                          const isSubActive = isActiveRoute(subItem.href);
                          return (
                            <NavLink
                              key={subItem.name}
                              to={subItem.href}
                              onClick={() => {
                                // Close popover when navigating to subitem
                                handleClosePopover();
                              }}
                              className={`group relative flex items-center px-2 py-0.5 text-xs font-medium rounded-lg transition-all duration-200 touch-manipulation ${
                                isSubActive
                                  ? 'bg-gradient-to-r from-gray-100 to-gray-200 text-gray-800 shadow-md border border-gray-300'
                                  : 'text-slate-500 active:bg-gradient-to-r active:from-slate-100 active:to-gray-200 active:text-gray-700 lg:hover:bg-gradient-to-r lg:hover:from-slate-100 lg:hover:to-gray-200 lg:hover:text-gray-700'
                              }`}
                            >
                              {isSubActive && (
                                <div className="absolute left-0 top-1/2 transform -translate-y-1/2 w-1 h-4 bg-gray-400 rounded-r-full" />
                              )}

                              <div className="flex-1 min-w-0">
                                <span className="font-semibold text-xs leading-tight truncate block">{subItem.name}</span>
                              </div>
                            </NavLink>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      <style>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(-50%) translateX(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(-50%) translateX(0);
          }
        }

        @keyframes expandDown {
          from {
            opacity: 0;
            max-height: 0;
          }
          to {
            opacity: 1;
            max-height: 1000px;
          }
        }

        .animate-slideIn {
          animation: slideIn 0.2s ease-out;
        }

        .animate-expandDown {
          animation: expandDown 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default SidebarGroup;
